import type { CoasterProject, TrackBehavior, TrackSection } from '../schema/index.ts';
import { getAuthoredDescriptor } from '../builder-v2/catalog.ts';
import { posesConnect } from '../builder-v2/fixed-grid.ts';

export const PHASE_2_NODE_LIMIT = 256;
export const PHASE_2_SCENERY_LIMIT = 128;
export const PHASE_2_MAX_TRACK_HEIGHT_M = 65;

export type TopologyStatus = 'incomplete' | 'open' | 'disconnected' | 'closed' | 'invalid';

export interface TopologyIssue {
  readonly code: string;
  readonly message: string;
  readonly severity: 'warning' | 'blocker';
  readonly nodeId?: string;
  readonly sectionId?: string;
}

export interface TopologyReport {
  readonly status: TopologyStatus;
  readonly rideable: boolean;
  readonly issues: readonly TopologyIssue[];
}

export function validateTrackTopology(project: CoasterProject): TopologyReport {
  const { nodes, sections, closed } = project.track;
  const issues: TopologyIssue[] = [];
  if (nodes.length > PHASE_2_NODE_LIMIT)
    issues.push({
      code: 'node_limit',
      message: `This editor supports up to ${PHASE_2_NODE_LIMIT} track points.`,
      severity: 'blocker',
    });
  if (project.scenery.length > PHASE_2_SCENERY_LIMIT)
    issues.push({
      code: 'scenery_limit',
      message: `This editor supports up to ${PHASE_2_SCENERY_LIMIT} scenery pieces.`,
      severity: 'blocker',
    });

  if (project.track.geometryKind === 'authored_grid_v2') {
    const byId = new Map(project.track.pieces.map((piece) => [piece.id, piece]));
    let damaged = false;
    for (const component of project.track.authoredTopology.components) {
      const pieces = component.pieceIds.map((pieceId) => byId.get(pieceId));
      if (pieces.some((piece) => !piece)) {
        damaged = true;
        issues.push({
          code: 'missing_component_piece',
          message: 'A construction component references a missing track piece.',
          severity: 'blocker',
        });
        continue;
      }
      for (let index = 1; index < pieces.length; index += 1)
        if (!posesConnect(pieces[index - 1]!.exitPose, pieces[index]!.entryPose)) {
          damaged = true;
          issues.push({
            code: 'component_pose_mismatch',
            message: 'Two neighboring authored pieces no longer share an exact connector.',
            severity: 'blocker',
            sectionId: pieces[index]!.sectionIds[0],
          });
        }
      if (
        component.closed &&
        pieces.length > 0 &&
        !posesConnect(pieces.at(-1)!.exitPose, pieces[0]!.entryPose)
      ) {
        damaged = true;
        issues.push({
          code: 'closed_component_pose_mismatch',
          message: 'The closed authored circuit has a damaged station seam.',
          severity: 'blocker',
        });
      }
    }
    if (damaged) return { status: 'invalid', rideable: false, issues };
    if (!project.track.pieces.length) {
      issues.push({
        code: 'needs_points',
        message: 'Add a station to start your track.',
        severity: 'blocker',
      });
      return { status: 'incomplete', rideable: false, issues };
    }
    const onlyComponent = project.track.authoredTopology.components[0];
    const singleClosed =
      project.track.authoredTopology.components.length === 1 &&
      onlyComponent?.closed === true &&
      closed;
    if (singleClosed) return { status: 'closed', rideable: true, issues };
    const firstPiece = onlyComponent ? byId.get(onlyComponent.pieceIds[0]!) : null;
    const ordinaryOpen =
      project.track.authoredTopology.components.length === 1 &&
      !onlyComponent?.closed &&
      getAuthoredDescriptor(firstPiece?.descriptorId ?? '')?.category === 'station';
    if (ordinaryOpen) {
      issues.push({
        code: 'open_track',
        message: 'Finish the open end back to the station before test riding.',
        severity: 'blocker',
      });
      return { status: 'open', rideable: false, issues };
    }
    const gapCount = Math.max(1, project.track.authoredTopology.components.length - 1);
    issues.push({
      code: 'disconnected_components',
      message: `Repair ${gapCount} track gap${gapCount === 1 ? '' : 's'} before testing or publishing.`,
      severity: 'blocker',
    });
    return { status: 'disconnected', rideable: false, issues };
  }

  const nodeIds = new Set<string>();
  for (const node of nodes) {
    if (nodeIds.has(node.id))
      issues.push({
        code: 'duplicate_node',
        message: 'Two track points share the same identity.',
        severity: 'blocker',
        nodeId: node.id,
      });
    nodeIds.add(node.id);
  }
  const sectionIds = new Set<string>();
  const edges = new Set<string>();
  const adjacency = new Map(nodes.map((node) => [node.id, new Set<string>()]));
  for (const section of sections) {
    if (sectionIds.has(section.id))
      issues.push({
        code: 'duplicate_section',
        message: 'Two track pieces share the same identity.',
        severity: 'blocker',
        sectionId: section.id,
      });
    sectionIds.add(section.id);
    if (!nodeIds.has(section.fromNodeId) || !nodeIds.has(section.toNodeId)) {
      issues.push({
        code: 'orphan_section',
        message: 'A track piece is disconnected from the main ride.',
        severity: 'blocker',
        sectionId: section.id,
      });
      continue;
    }
    if (section.fromNodeId === section.toNodeId) {
      issues.push({
        code: 'self_loop',
        message: 'A track piece loops back to the same point.',
        severity: 'blocker',
        sectionId: section.id,
      });
      continue;
    }
    const edgeKey = [section.fromNodeId, section.toNodeId].sort().join('|');
    if (edges.has(edgeKey))
      issues.push({
        code: 'duplicate_edge',
        message: 'Two track pieces connect the same pair of points.',
        severity: 'blocker',
        sectionId: section.id,
      });
    edges.add(edgeKey);
    adjacency.get(section.fromNodeId)?.add(section.toNodeId);
    adjacency.get(section.toNodeId)?.add(section.fromNodeId);
  }

  if (nodes.length > 0) {
    const visited = new Set<string>();
    const queue = [nodes[0]!.id];
    while (queue.length) {
      const nodeId = queue.shift()!;
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);
      adjacency.get(nodeId)?.forEach((neighbor) => queue.push(neighbor));
    }
    if (visited.size !== nodes.length)
      issues.push({
        code: 'disconnected_component',
        message: 'Remove track pieces that are not connected to the main ride.',
        severity: 'blocker',
      });
    const degrees = nodes.map((node) => adjacency.get(node.id)?.size ?? 0);
    const invalidDegrees = closed
      ? degrees.some((degree) => degree !== 2)
      : nodes.length >= 2 &&
        (degrees.filter((degree) => degree === 1).length !== 2 ||
          degrees.some((degree) => degree < 1 || degree > 2));
    if (invalidDegrees)
      issues.push({
        code: 'branching_or_orphan',
        message: closed
          ? 'A closed coaster must be one loop with no branches or unused pieces.'
          : 'An open coaster must be one chain with exactly two build ends.',
        severity: 'blocker',
      });
  }

  const expectedSections = closed ? nodes.length : Math.max(0, nodes.length - 1);
  if (sections.length !== expectedSections)
    issues.push({
      code: 'section_count',
      message: closed
        ? 'A closed track needs one section between every pair of points.'
        : 'An open track needs one section between each neighboring point.',
      severity: 'blocker',
    });

  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index]!;
    const from = nodes[index];
    const to = nodes[(index + 1) % Math.max(1, nodes.length)];
    if (!from || !to || section.fromNodeId !== from.id || section.toNodeId !== to.id)
      issues.push({
        code: 'section_order',
        message: `Section ${index + 1} is not connected to its neighboring track points.`,
        severity: 'blocker',
        sectionId: section.id,
      });
  }

  if (nodes.length < 4) {
    issues.push({
      code: 'needs_points',
      message:
        nodes.length === 0
          ? 'Add a station to start your track.'
          : `Add ${4 - nodes.length} more track point${nodes.length === 3 ? '' : 's'} to test ride.`,
      severity: 'blocker',
    });
    return {
      status: issues.some((issue) => issue.severity === 'blocker' && issue.code !== 'needs_points')
        ? 'invalid'
        : 'incomplete',
      rideable: false,
      issues,
    };
  }

  const invalid = issues.some((issue) => issue.severity === 'blocker');
  if (!invalid && !closed)
    issues.push({
      code: 'open_track',
      message: 'Finish the open end back to the station before test riding.',
      severity: 'blocker',
    });
  return {
    status: invalid ? 'invalid' : closed ? 'closed' : 'open',
    rideable: !invalid && closed,
    issues,
  };
}

export function sameBehavior(a: TrackBehavior, b: TrackBehavior): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function mergeBehavior(
  incoming: TrackBehavior,
  outgoing: TrackBehavior,
): TrackBehavior | null {
  if (sameBehavior(incoming, outgoing)) return structuredClone(incoming);
  if (incoming.kind === 'normal') return structuredClone(outgoing);
  if (outgoing.kind === 'normal') return structuredClone(incoming);
  return null;
}

export function makeOrderedSection(
  id: string,
  fromNodeId: string,
  toNodeId: string,
  behavior: TrackBehavior = { kind: 'normal' },
): TrackSection {
  return { id, fromNodeId, toNodeId, behavior };
}
